<script lang="ts">
  import Icon from "../Icon.svelte";

  interface Props {
    data: {
      address: string;
      ensName?: string;
      balance: string;
      usdValue: string;
      transactionCount: number;
      tokens: Array<{ name: string; balance: string; usdValue: string }>;
      recentActivity: Array<{ type: string; description: string; time: string }>;
    };
  }

  let { data }: Props = $props();

  let shortAddress = $derived(
    `${data.address.slice(0, 6)}...${data.address.slice(-4)}`
  );
</script>

<div class="address-result fade-in-up">
  <header class="result-header">
    <div class="avatar">
      <Icon name="user" size={24} />
    </div>
    <div class="result-title">
      <div class="title-row">
        {#if data.ensName}
          <h2>{data.ensName}</h2>
        {:else}
          <h2>{shortAddress}</h2>
        {/if}
        <button class="copy-btn" title="Copy address">
          <Icon name="copy" size={14} />
        </button>
      </div>
      {#if data.ensName}
        <span class="result-address">{data.address}</span>
      {/if}
    </div>
    <div class="balance-display">
      <span class="balance-value">{data.balance}</span>
      <span class="balance-usd">{data.usdValue}</span>
    </div>
  </header>

  <!-- Recent Activity with AI-generated memos -->
  <section class="activity-section">
    <h3 class="section-title">
      <Icon name="clock" size={14} />
      Recent Activity
    </h3>
    <div class="activity-list">
      {#each data.recentActivity as activity}
        <div class="activity-item">
          <div class="activity-type" class:sent={activity.type === "Sent"} class:swap={activity.type === "Swap"} class:deposit={activity.type === "Deposit"}>
            {activity.type}
          </div>
          <span class="activity-desc">{activity.description}</span>
          <span class="activity-time">{activity.time}</span>
        </div>
      {/each}
    </div>
  </section>

  <!-- Token Holdings -->
  <section class="tokens-section">
    <h3 class="section-title">
      <Icon name="coins" size={14} />
      Token Holdings
    </h3>
    <div class="tokens-list">
      {#each data.tokens as token}
        <div class="token-item">
          <div class="token-icon">
            <Icon name="coins" size={16} />
          </div>
          <span class="token-name">{token.name}</span>
          <span class="token-balance">{token.balance}</span>
          <span class="token-usd">{token.usdValue}</span>
        </div>
      {/each}
    </div>
  </section>

  <div class="actions">
    <button class="action-btn primary">
      <Icon name="arrow-right-left" size={16} />
      View All Transactions
    </button>
    <button class="action-btn">
      <Icon name="external-link" size={16} />
      Etherscan
    </button>
  </div>
</div>

<style>
  .address-result {
    background: var(--bg-secondary);
    border: 1px solid var(--border-subtle);
    border-radius: var(--radius-lg);
    overflow: hidden;
  }

  .result-header {
    display: flex;
    align-items: flex-start;
    gap: var(--space-4);
    padding: var(--space-6);
    border-bottom: 1px solid var(--border-subtle);
  }

  .avatar {
    width: 48px;
    height: 48px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, var(--accent-primary), var(--accent-secondary));
    border-radius: var(--radius-full);
    color: white;
    flex-shrink: 0;
  }

  .result-title {
    flex: 1;
    min-width: 0;
  }

  .title-row {
    display: flex;
    align-items: center;
    gap: var(--space-2);
  }

  .title-row h2 {
    font-size: 1.25rem;
    font-weight: 600;
    color: var(--text-primary);
  }

  .copy-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: var(--radius-sm);
    color: var(--text-tertiary);
    transition: all var(--transition-fast);
  }

  .copy-btn:hover {
    background: var(--bg-hover);
    color: var(--text-primary);
  }

  .result-address {
    font-family: var(--font-mono);
    font-size: 0.75rem;
    color: var(--text-tertiary);
    margin-top: var(--space-1);
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .balance-display {
    text-align: right;
    flex-shrink: 0;
  }

  .balance-value {
    display: block;
    font-size: 1.25rem;
    font-weight: 700;
    font-family: var(--font-mono);
    color: var(--text-primary);
  }

  .balance-usd {
    display: block;
    font-size: 0.875rem;
    color: var(--text-secondary);
    margin-top: 2px;
  }

  .activity-section,
  .tokens-section {
    padding: var(--space-5);
    border-bottom: 1px solid var(--border-subtle);
  }

  .section-title {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    font-size: 0.8125rem;
    font-weight: 600;
    color: var(--text-secondary);
    margin-bottom: var(--space-4);
  }

  .activity-list {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }

  .activity-item {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    padding: var(--space-3);
    background: var(--bg-tertiary);
    border-radius: var(--radius-md);
  }

  .activity-type {
    font-size: 0.6875rem;
    font-weight: 600;
    padding: 2px 8px;
    border-radius: var(--radius-full);
    background: var(--bg-hover);
    color: var(--text-secondary);
    text-transform: uppercase;
  }

  .activity-type.sent {
    background: rgba(239, 68, 68, 0.1);
    color: var(--accent-danger);
  }

  .activity-type.swap {
    background: rgba(139, 92, 246, 0.1);
    color: var(--accent-secondary);
  }

  .activity-type.deposit {
    background: rgba(34, 197, 94, 0.1);
    color: var(--accent-success);
  }

  .activity-desc {
    flex: 1;
    font-size: 0.875rem;
    color: var(--text-primary);
  }

  .activity-time {
    font-size: 0.75rem;
    color: var(--text-tertiary);
  }

  .tokens-list {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }

  .token-item {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    padding: var(--space-3);
    background: var(--bg-tertiary);
    border-radius: var(--radius-md);
  }

  .token-icon {
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--bg-hover);
    border-radius: var(--radius-full);
    color: var(--text-tertiary);
  }

  .token-name {
    font-size: 0.875rem;
    font-weight: 500;
    color: var(--text-primary);
    min-width: 60px;
  }

  .token-balance {
    flex: 1;
    font-size: 0.875rem;
    font-family: var(--font-mono);
    color: var(--text-secondary);
    text-align: right;
  }

  .token-usd {
    font-size: 0.8125rem;
    color: var(--text-tertiary);
    min-width: 80px;
    text-align: right;
  }

  .actions {
    display: flex;
    gap: var(--space-3);
    padding: var(--space-5);
  }

  .action-btn {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    padding: var(--space-3) var(--space-4);
    border-radius: var(--radius-md);
    font-size: 0.875rem;
    font-weight: 500;
    color: var(--text-secondary);
    background: var(--bg-tertiary);
    border: 1px solid var(--border-subtle);
    transition: all var(--transition-fast);
  }

  .action-btn:hover {
    border-color: var(--border-default);
    color: var(--text-primary);
  }

  .action-btn.primary {
    background: var(--accent-primary);
    border-color: var(--accent-primary);
    color: white;
  }

  .action-btn.primary:hover {
    background: #2563eb;
  }
</style>
